import React, { useMemo, useRef, useState } from "react";
import { ScanLine, CheckCircle2, XCircle, Trash2, Plus, Lock } from "lucide-react";
import QrScanner from "./QrScanner";

const GOLD = "bg-[#a67c00] hover:bg-[#8a6600] text-white";
const inr = (n) => "₹" + Number(n || 0).toLocaleString("en-IN");
const btn = "px-3 py-2 rounded-md text-sm font-medium disabled:opacity-50";

/**
 * Batch coupon scan (Salesperson + Distributor).
 * Props: retailers[], previewFn(body), submitFn(body), gps, deviceId
 * Flow: select retailer -> Start (locked) -> scan many (validated, NOT credited)
 *       -> Confirm credits all to that retailer. Cash and Points totalled separately.
 */
export default function BatchScan({ retailers = [], previewFn, submitFn, gps, deviceId }) {
  const [retailer, setRetailer] = useState(null);
  const [search, setSearch] = useState("");
  const [items, setItems] = useState([]);
  const [code, setCode] = useState("");
  const [scanOpen, setScanOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [notice, setNotice] = useState(null);
  const [summary, setSummary] = useState(null);
  const busyRef = useRef(false);
  const lastOk = useRef(false);
  const camWanted = useRef(false);
  const itemsRef = useRef([]);
  itemsRef.current = items;

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const list = !q ? retailers : retailers.filter(r =>
      (r.name || "").toLowerCase().includes(q) || (r.phone || "").includes(q)
      || (r.city || "").toLowerCase().includes(q) || (r.shop_name || "").toLowerCase().includes(q));
    return list.slice(0, 50);
  }, [retailers, search]);

  const cashTotal = items.filter(i => i.type === "cash").reduce((s, i) => s + i.value, 0);
  const pointTotal = items.filter(i => i.type !== "cash").reduce((s, i) => s + i.value, 0);

  const addCoupon = async ({ qr, code: c }) => {
    lastOk.current = false;
    const key = (qr || c || "").trim();
    if (!key || busyRef.current || !retailer) return;
    if (itemsRef.current.some(i => i.key === key)) {
      setNotice({ ok: false, msg: "This coupon is already in the list — skipped" }); return;
    }
    busyRef.current = true; setBusy(true); setNotice(null);
    try {
      const r = await previewFn({
        retailer_id: retailer.id, qr_payload: qr || undefined, coupon_code: c || undefined,
        gps_lat: gps?.lat, gps_lng: gps?.lng, device_id: deviceId,
      });
      const p = r.preview || {};
      if (r.fraud || p.fraud) {
        setNotice({ ok: false, msg: "Rejected: " + String(r.fraud_reason || p.fraud_reason || "invalid").replace(/_/g, " ") });
        return;
      }
      const serial = p.coupon_serial || p.visible_serial || key;
      if (itemsRef.current.some(i => i.serial === serial)) {
        setNotice({ ok: false, msg: `${serial} is already in the list — skipped` }); return;
      }
      setItems(prev => [...prev, {
        key, serial, qr: qr || undefined, code: c || undefined,
        type: p.coupon_type, value: Number(p.coupon_value || 0), box: p.box_number,
      }]);
      lastOk.current = true;
      setNotice({ ok: true, msg: `${serial} added` });
    } catch (e) {
      setNotice({ ok: false, msg: e?.response?.data?.detail || "Validation failed" });
    } finally { busyRef.current = false; setBusy(false); }
  };

  const onCamResult = async (text) => {
    setScanOpen(false);
    await addCoupon({ qr: text });
    if (camWanted.current && lastOk.current) setTimeout(() => setScanOpen(true), 1200);
    else camWanted.current = false;
  };
  const openCam = () => { camWanted.current = true; setScanOpen(true); };
  const closeCam = () => { camWanted.current = false; setScanOpen(false); };

  const addManual = async () => {
    const c = code.trim().toUpperCase();
    if (!c) return;
    await addCoupon({ code: c });
    setCode("");
  };

  const confirm = async () => {
    if (!items.length || confirming) return;
    closeCam(); setConfirming(true); setNotice(null);
    const ok = [], failed = [];
    for (const it of itemsRef.current) {
      try {
        await submitFn({
          retailer_id: retailer.id, qr_payload: it.qr, coupon_code: it.code,
          gps_lat: gps?.lat, gps_lng: gps?.lng, device_id: deviceId,
        });
        ok.push(it);
      } catch (e) {
        failed.push({ ...it, error: e?.response?.data?.detail || "Failed" });
      }
    }
    setItems(failed);
    setSummary({
      cash: ok.filter(i => i.type === "cash").reduce((s, i) => s + i.value, 0),
      points: ok.filter(i => i.type !== "cash").reduce((s, i) => s + i.value, 0),
      count: ok.length, failed,
    });
    setConfirming(false);
  };

  const newSession = () => { setRetailer(null); setItems([]); setSummary(null); setNotice(null); setSearch(""); };

  // ---------- STEP 1: select retailer ----------
  if (!retailer) {
    return (
      <div className="border rounded-lg p-5 bg-white max-w-xl" data-testid="batch-select">
        <h3 className="font-bold text-slate-900 mb-2">Step 1 · Select Retailer</h3>
        <input className="w-full border rounded-md px-3 py-2 text-sm mb-2" placeholder="Search name / phone / city / shop"
               value={search} onChange={e => setSearch(e.target.value)} />
        <div className="max-h-72 overflow-auto divide-y border rounded-md">
          {filtered.map(r => (
            <button key={r.id} className="w-full text-left px-3 py-2 hover:bg-amber-50 text-sm"
                    onClick={() => setRetailer(r)} data-testid={`batch-ret-${r.id}`}>
              <b>{r.name}</b> <span className="text-slate-500">— {r.shop_name || "Retailer"}{r.city ? `, ${r.city}` : ""}</span>
            </button>
          ))}
          {!filtered.length && <div className="p-3 text-sm text-slate-500">No retailer found</div>}
        </div>
      </div>
    );
  }

  // ---------- STEP 2: scanning ----------
  return (
    <div className="border rounded-lg p-5 bg-white max-w-xl" data-testid="batch-scan">
      <div className="flex items-center gap-2 mb-3 p-2 rounded bg-emerald-50 border border-emerald-200 text-sm">
        <Lock size={14} className="text-emerald-700" />
        <span>Retailer: <b>{retailer.name}</b> {retailer.shop_name ? `— ${retailer.shop_name}` : ""}</span>
        {!items.length && !confirming && (
          <button className="ml-auto text-xs underline text-slate-600" onClick={newSession}>Change</button>
        )}
      </div>

      {summary && (
        <div className={`mb-3 p-3 rounded border text-sm ${summary.failed.length ? "bg-amber-50 border-amber-300" : "bg-emerald-50 border-emerald-300"}`} data-testid="batch-summary">
          <div className="font-semibold flex items-center gap-1"><CheckCircle2 size={16} /> {summary.count} coupon credited to {retailer.name}</div>
          <div>Cash: <b>{inr(summary.cash)}</b> &nbsp;•&nbsp; Points: <b>{summary.points}</b></div>
          {summary.failed.length > 0 && (
            <div className="mt-1 text-rose-700">{summary.failed.length} failed (see list below) — press Confirm again to retry, or remove them.</div>
          )}
          <button className={`${btn} ${GOLD} mt-2`} onClick={newSession}>New session / Next retailer</button>
        </div>
      )}

      {!(summary && !summary.failed.length) && (
        <>
          <button className={`${btn} ${GOLD} w-full mb-2 flex items-center justify-center gap-2`} disabled={busy || confirming}
                  onClick={openCam} data-testid="batch-open-camera">
            <ScanLine size={16} /> {items.length ? "+ Add more (Camera)" : "Scan with Camera"}
          </button>
          <div className="flex gap-2 mb-2">
            <input className="flex-1 border rounded-md px-3 py-2 text-sm font-mono" placeholder="Coupon code manually"
                   value={code} onChange={e => setCode(e.target.value.toUpperCase())}
                   onKeyDown={e => e.key === "Enter" && addManual()} />
            <button className={`${btn} border`} disabled={busy || confirming || !code.trim()} onClick={addManual}>
              <Plus size={14} className="inline" /> Add
            </button>
          </div>
        </>
      )}

      {notice && (
        <div className={`mb-2 text-sm px-3 py-2 rounded border ${notice.ok ? "bg-emerald-50 border-emerald-200 text-emerald-800" : "bg-rose-50 border-rose-300 text-rose-700"}`}>
          {notice.msg}
        </div>
      )}

      {items.length > 0 && (
        <div className="mt-2" data-testid="batch-list">
          <div className="text-xs font-semibold text-slate-500 mb-1">SCANNED ({items.length})</div>
          <div className="border rounded-md divide-y max-h-64 overflow-auto">
            {items.map((it, idx) => (
              <div key={it.key} className="flex items-center gap-2 px-3 py-2 text-sm">
                <span className="text-slate-400 w-5">{idx + 1}</span>
                <span className="font-mono font-semibold">{it.serial}</span>
                <span className={`text-xs px-1.5 py-0.5 rounded ${it.type === "cash" ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"}`}>
                  {it.type === "cash" ? inr(it.value) : `${it.value} pts`}
                </span>
                {it.error && <span className="text-xs text-rose-600 flex items-center gap-1"><XCircle size={12} />{it.error}</span>}
                <button className="ml-auto text-slate-400 hover:text-rose-600" disabled={confirming}
                        onClick={() => setItems(prev => prev.filter(x => x.key !== it.key))}>
                  <Trash2 size={14} />
                </button>
              </div>
            ))}
          </div>
          <div className="grid grid-cols-2 gap-2 mt-3 text-sm">
            <div className="p-2 rounded bg-emerald-50 border border-emerald-200">Total Cash<div className="text-lg font-bold text-emerald-700">{inr(cashTotal)}</div></div>
            <div className="p-2 rounded bg-amber-50 border border-amber-200">Total Points<div className="text-lg font-bold text-[#a67c00]">{pointTotal} pts</div></div>
          </div>
          <button className={`${btn} ${GOLD} w-full mt-3`} disabled={confirming || busy} onClick={confirm} data-testid="batch-confirm">
            {confirming ? "Crediting…" : `Confirm & Credit ${retailer.name}`}
          </button>
        </div>
      )}

      <QrScanner open={scanOpen} onClose={closeCam} onResult={onCamResult} />
    </div>
  );
}
